import React, { useState, useEffect, useRef } from 'react';
import { 
  LayoutDashboard, 
  PenTool,
  Bell, 
  Calendar, 
  BarChart3, 
  Sparkles,
  X
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLinkedInAccount } from '../hooks/useLinkedInAccount'; // ✅ ADD THIS

const Sidebar = ({ activeTab, setActiveTab = () => {}, sidebarOpen, setSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { accountId } = useLinkedInAccount(); // ✅ ADD THIS
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasFetchedRef = useRef(false);

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { id: 'create-post', label: 'create post', icon: PenTool, path: '/create-post' },
    { id: 'scheduled', label: 'Scheduled Posts', icon: Calendar, path: '/scheduled-posts' },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, path: '/analytics' },
  ];

  const getUserId = () => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      console.error('❌ No userId found in localStorage');
      return null;
    }
    return userId;
  };

  // ✅ UPDATED: Fetch when accountId changes
  useEffect(() => {
    if (accountId) {
      fetchNotifications();
      hasFetchedRef.current = true;
      
      const interval = setInterval(fetchNotifications, 300000);
      return () => clearInterval(interval);
    }
  }, [accountId]); // ✅ Re-fetch when account changes

  const fetchNotifications = async () => {
    try {
      const userId = getUserId();
      
      if (!userId || !accountId) { // ✅ Check both
        console.log('Missing userId or accountId, skipping notifications fetch');
        setLoading(false);
        return;
      }

      console.log('🔔 Fetching notifications for:', { userId, accountId });

      const response = await fetch('https://n8n.acespireconsulting.com/webhook/notifications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          userId,
          linkedinAccountId: accountId // ✅ ADD THIS
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Notifications received:', data.notifications?.length || 0);
        setNotifications(data.notifications || []);
      } else {
        console.error('❌ Notifications fetch failed:', response.status);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshNotifications = () => {
    setLoading(true);
    fetchNotifications();
  };

  const moreAIItems = [
    { label: 'Hireon AI', url: 'https://hireon.acespireconsulting.com/login', active: true },
    { label: 'Nextlead AI', url: 'https://nextlead.acespireconsulting.com/', active: true },
  ];

  const handleSidebarItemClick = (itemId, path) => {
    if (typeof setActiveTab === 'function') {
      setActiveTab(itemId);
    }
    
    if (typeof setSidebarOpen === 'function') {
      setSidebarOpen(false);
    }
    
    navigate(path);
  };

  const handleExternalLinkClick = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    
    if (typeof setSidebarOpen === 'function') {
      setSidebarOpen(false);
    }
  };

  const getCurrentActiveTab = () => {
    if (activeTab) return activeTab;
    
    const path = location.pathname;
    if (path === '/') return 'create-post';
    if (path === '/dashboard') return 'dashboard';
    if (path === '/create-post') return 'create-post';
    if (path === '/scheduled-posts') return 'scheduled';
    if (path === '/analytics') return 'analytics';
    if (path === '/analytics/visitors') return 'analytics';
    if (path === '/analytics/followers') return 'analytics';
    return 'create-post';
  };

  const currentActiveTab = getCurrentActiveTab();

  return (
    <>
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen && setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50
        w-64 text-white
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col h-screen
      `} style={{backgroundImage: 'url("/sidebarbg.png")', backgroundSize: 'cover', backgroundPosition: 'center'}}>
        <div className="p-4 lg:p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-2 lg:mr-3">
                <Sparkles className="w-4 h-4 lg:w-6 lg:h-6" />
              </div>
              <div>
                <h1 className="font-semibold text-sm lg:text-lg">Digimark AI by Acespire</h1>
              </div>
            </div>
            <button 
              className="lg:hidden text-white"
              onClick={() => setSidebarOpen && setSidebarOpen(false)}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>            

        <div className="flex-1 overflow-y-auto px-4 lg:px-4 no-scrollbar">
          <nav className="space-y-2 mb-2">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleSidebarItemClick(item.id, item.path)}
                className={`w-full flex items-center px-3 py-2.5 lg:px-4 lg:py-3 rounded-lg text-left transition-colors text-sm lg:text-base ${
                  currentActiveTab === item.id 
                    ? 'bg-white bg-opacity-20' 
                    : 'hover:bg-white hover:bg-opacity-10'
                }`}
              >
                <item.icon className="w-4 h-4 lg:w-5 lg:h-5 mr-2 lg:mr-3 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
                {item.id !== 'dashboard' && (
                  <div className="ml-auto w-2 h-2 bg-green-400 rounded-full flex-shrink-0"></div>
                )}
              </button>
            ))}
          </nav>

          <div className="mb-4">
            <h3 className="text-xs lg:text-sm font-medium mb-3 opacity-70">More AI</h3>
            {moreAIItems.map((item, index) => (
              <button
                key={index}
                onClick={() => handleExternalLinkClick(item.url)}
                className="w-full flex items-center px-3 py-2 lg:px-4 lg:py-2 rounded-lg text-left hover:bg-white hover:bg-opacity-10 mb-1 text-sm transition-colors"
              >
                <span className="truncate">{item.label}</span>
                <div className="ml-auto w-2 h-2 bg-green-400 rounded-full flex-shrink-0"></div>
              </button>
            ))}
          </div>

          {/* Notifications Section */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs lg:text-sm font-medium opacity-70">Notifications</h3>
              <div className="flex items-center gap-2">
                <button 
                  onClick={handleRefreshNotifications}
                  className="opacity-70 hover:opacity-100 transition-opacity"
                  title="Refresh notifications"
                >
                  <Bell className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {loading ? (
              <div className="text-xs opacity-70 px-3 py-2">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="text-xs opacity-70 px-3 py-2">No new notifications</div>
            ) : (
              notifications.map((notif) => (
                <button
                  key={notif.id}
                  onClick={() => notif.url && window.open(notif.url, '_blank')}
                  className="w-full flex items-start px-3 py-2 rounded-lg text-left hover:bg-white hover:bg-opacity-10 mb-1 group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs truncate">{notif.text}</p>
                  </div>
                  {!notif.read && (
                    <div className="ml-2 w-2 h-2 bg-green-400 rounded-full flex-shrink-0 mt-1"></div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

<div className="p-2 lg:p-2 flex-shrink-0 flex justify-center mb-2">
  <div className="bg-white rounded-full px-14 py-1 shadow-sm">
    <img
      src="/AcespireLogo.png"
      alt="Acespire Consulting Logo"
      className="h-12 object-contain"
    />
  </div>
</div>

      </div>
    </>
  );
};

export default Sidebar;